import React, { useEffect, useState } from 'react';
import {
  cancelRazorpayOrder,
  createAITopupOrder,
  createRazorpayOrder,
  fetchBillingInvoices,
  fetchAIUsage,
  fetchBillingUsage,
  verifyAITopupPayment,
  verifyRazorpayPayment,
} from '../services/api';
import DashboardContainer from '../components/layout/DashboardContainer';
import { useChatContext } from '../context/ChatContext';
import { useNotification } from '../context/NotificationContext';
import { DollarSign, MessageCircle, Receipt } from 'lucide-react';
import { getSubscriptionAccessState, getTrialDaysLeft } from '../utils/subscriptionAccess';

const PLAN_PRICING = {
  starter: { monthly: 999, yearly: 9999 },
  business: { monthly: 2499, yearly: 24999 },
  enterprise: { monthly: 4999, yearly: 49999 },
};

const getRazorpayErrorMessage = (err, fallback) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  if (status === 504) {
    return detail || 'The server timed out while contacting Razorpay. Please try again in a moment.';
  }

  if (status === 502) {
    return detail || 'The server could not reach Razorpay right now. Please try again shortly.';
  }

  return detail || err?.message || fallback;
};

const BillingPage = () => {
  const { showSuccess, showError, showWarning } = useNotification();
  const { clientProfile } = useChatContext();
  const accessState = getSubscriptionAccessState(clientProfile);

  // Usage tracking
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Razorpay checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [topupAmount, setTopupAmount] = useState(2000);
  const [topupLoading, setTopupLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const usagePromise = fetchBillingUsage();
        const aiPromise = fetchAIUsage();
        const invPromise = fetchBillingInvoices();
        const [billingRes, aiRes, invRes] = await Promise.allSettled([usagePromise, aiPromise, invPromise]);
        if (billingRes.status === 'fulfilled') setData(billingRes.value.data);
        if (aiRes.status === 'fulfilled') setAiUsage(aiRes.value.data);
        if (invRes.status === 'fulfilled') setInvoices(invRes.value.data || []);
      } catch (e) {
        setError('Failed to load usage data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!clientProfile) return;
    if (clientProfile.plan) setSelectedPlan(clientProfile.plan);
    if (clientProfile.billing_cycle) setBillingCycle(clientProfile.billing_cycle);
    if (clientProfile.subscription_status === 'payment_required') {
      setShowCheckout(true);
    }
  }, [clientProfile]);

  const handleCheckout = () => {
    setShowCheckout(true);
  };

  const loadRazorpayScript = () => new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout SDK.'));
    document.body.appendChild(script);
  });

  const handleStartPayment = async () => {
    setSubmitting(true);
    try {
      await loadRazorpayScript();
      const { data: order } = await createRazorpayOrder({
        plan: selectedPlan,
        billing_cycle: billingCycle,
      });

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Blastmate',
        description: `${selectedPlan} Plan (${billingCycle})`,
        order_id: order.order_id,
        prefill: {
          name: clientProfile?.name || '',
          contact: clientProfile?.owner_phone_number || '',
        },
        handler: async (response) => {
          try {
            await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            showSuccess('Payment successful. Subscription activated.');
            setShowCheckout(false);
            window.location.reload();
          } catch (err) {
            showError(err.response?.data?.detail || 'Payment verification failed.');
          }
        },
        modal: {
          ondismiss: async () => {
            try {
              await cancelRazorpayOrder({ razorpay_order_id: order.order_id });
              setShowCheckout(false);
              showWarning('Payment cancelled. You can restart checkout anytime.');
              window.dispatchEvent(new CustomEvent('auth-token-changed'));
            } catch (cancelErr) {
              showError(cancelErr.response?.data?.detail || 'Unable to cancel the unfinished payment cleanly.');
            }
          },
        },
        theme: { color: '#6366f1' },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        const reason = resp?.error?.description || 'Payment failed. Please try again.';
        showError(reason);
      });
      rzp.open();
    } catch (err) {
      showError(getRazorpayErrorMessage(err, 'Unable to start Razorpay checkout.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartTopup = async () => {
    if (!topupAmount || Number(topupAmount) < 2000) {
      showWarning('Minimum top-up is INR 2000.');
      return;
    }
    setTopupLoading(true);
    try {
      await loadRazorpayScript();
      const { data: order } = await createAITopupOrder({ amount_inr: Number(topupAmount) });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Blastmate',
        description: 'AI Top-up Credits',
        order_id: order.order_id,
        handler: async (response) => {
          try {
            await verifyAITopupPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            showSuccess('Top-up added successfully.');
            window.location.reload();
          } catch (err) {
            showError(err.response?.data?.detail || 'Top-up verification failed.');
          }
        },
        modal: {
          ondismiss: () => showWarning('Top-up cancelled.'),
        },
        theme: { color: '#6366f1' },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      showError(getRazorpayErrorMessage(err, 'Unable to start top-up payment.'));
    } finally {
      setTopupLoading(false);
    }
  };

  const statusMap = {
    'payment_required': 'Payment Required',
    'trial': 'Trial Active',
    'pending': 'Payment In Progress',
    'active': 'Subscription Active',
    'expired': 'Subscription Expired'
  };

  return (
    <DashboardContainer>
      <div className="billing-page">
        {/* Subscription Header */}
        <div className="billing-header">
          <div>
            <h1>Subscription & Billing</h1>
            <p>Manage your plan and monitor messaging usage.</p>
          </div>
        </div>

        {/* Current Plan Overview */}
        <div style={{ padding: '24px', background: 'white', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h2 style={{ fontSize: '1.25rem', marginBottom: '4px' }}>
                Plan: <span style={{ color: 'var(--primary)' }}>
                  {clientProfile?.plan === 'starter' ? 'Starter' : 
                   clientProfile?.plan === 'business' ? 'Business' : 
                   clientProfile?.plan === 'enterprise' ? 'Enterprise' : 'Starter'}
                </span>
              </h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Status: <strong>{statusMap[accessState.effectiveStatus] || 'Trial'}</strong>
              </p>
              {(accessState.trial || accessState.pendingDuringTrial) && (
                <p style={{ color: 'var(--text-secondary)' }}>
                  Free trial active - {getTrialDaysLeft(clientProfile)} days remaining.
                </p>
              )}
              {accessState.pendingDuringTrial && (
                <p style={{ color: 'var(--text-secondary)' }}>
                  You can continue your unfinished payment from this page anytime without losing trial access.
                </p>
              )}
              {(() => {
                const expiry = clientProfile?.subscription_expires_at
                  ? new Date(clientProfile.subscription_expires_at)
                  : null;

                if (!expiry) return null;

                return (
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Expires: {expiry.toLocaleString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                );
              })()}
            </div>

            {clientProfile?.subscription_status !== 'active' && (
              <button className="admin-btn primary" onClick={handleCheckout} style={{ padding: '10px 24px', fontSize: '1rem' }}>
                {clientProfile?.subscription_status === 'pending' ? 'Continue Payment' : 'Activate Plan'}
              </button>
            )}
            {clientProfile?.subscription_status === 'active' && (
              <button className="admin-btn primary" onClick={handleCheckout} style={{ padding: '10px 24px', fontSize: '1rem' }}>
                Renew / Change Plan
              </button>
            )}
          </div>
        </div>

        {/* Razorpay Checkout Modal */}
        {showCheckout && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'white', padding: '32px', borderRadius: '8px', width: '100%', maxWidth: '500px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              <h2>Select Plan</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>
                Select your billing cycle and continue with secure Razorpay checkout.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' }}>
                {['starter', 'business', 'enterprise'].map((plan) => (
                  <button
                    key={plan}
                    type="button"
                    className="admin-btn"
                    onClick={() => setSelectedPlan(plan)}
                    style={{
                      border: `2px solid ${selectedPlan === plan ? 'var(--primary)' : 'var(--border)'}`,
                      textTransform: 'capitalize',
                    }}
                  >
                    {plan}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <div
                  onClick={() => setBillingCycle('monthly')}
                  style={{ flex: 1, padding: '16px', border: `2px solid ${billingCycle === 'monthly' ? 'var(--primary)' : 'var(--border)'}`, borderRadius: '8px', cursor: 'pointer', textAlign: 'center' }}
                >
                  <h3 style={{ margin: '0 0 8px 0' }}>Monthly</h3>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>₹{PLAN_PRICING[selectedPlan]?.monthly || 999}</div>
                </div>
                <div
                  onClick={() => setBillingCycle('yearly')}
                  style={{ flex: 1, padding: '16px', border: `2px solid ${billingCycle === 'yearly' ? 'var(--primary)' : 'var(--border)'}`, borderRadius: '8px', cursor: 'pointer', textAlign: 'center' }}
                >
                  <h3 style={{ margin: '0 0 8px 0' }}>Yearly <span style={{ fontSize: '0.75rem', background: '#ecfdf5', color: '#059669', padding: '2px 6px', borderRadius: '12px', verticalAlign: 'middle' }}>Save 17%</span></h3>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>₹{PLAN_PRICING[selectedPlan]?.yearly || 9999}</div>
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', textAlign: 'center', marginBottom: '24px' }}>
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                  Razorpay supports UPI, cards, netbanking and wallets. You will complete payment in the Razorpay popup.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="admin-btn primary" style={{ flex: 1 }} disabled={submitting} onClick={handleStartPayment}>
                  {submitting ? 'Starting...' : 'Pay with Razorpay'}
                </button>
                <button type="button" className="admin-btn" onClick={() => setShowCheckout(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Existing Stats/Usage Tracking below */}
        {clientProfile?.plan === 'enterprise' && aiUsage && (
          <div style={{ padding: '24px', background: 'white', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '8px' }}>AI Usage (Current Cycle)</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
              {aiUsage.used_tokens?.toLocaleString()} / {aiUsage.available_tokens?.toLocaleString()} tokens used ({aiUsage.usage_percent}%)
            </p>
            <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '99px', marginBottom: '16px' }}>
              <div style={{ width: `${Math.min(aiUsage.usage_percent || 0, 100)}%`, height: '100%', background: aiUsage.blocked ? '#ef4444' : '#6366f1', borderRadius: '99px' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                min={2000}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                className="input"
                style={{ maxWidth: '180px' }}
              />
              <button type="button" className="admin-btn primary" disabled={topupLoading} onClick={handleStartTopup}>
                {topupLoading ? 'Starting...' : 'Buy AI Top-up'}
              </button>
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>Minimum top-up INR 2000. Add higher amount anytime.</small>
          </div>
        )}

        {loading ? (
          <div className="billing-loading">Loading messaging usage...</div>
        ) : error ? (
          <div className="auth-error">{error}</div>
        ) : data ? (
          <>
            <h3 style={{ marginTop: '32px', marginBottom: '16px' }}>Meta WhatsApp Usage Estimated</h3>
            <div className="billing-stats">
              <div className="billing-stat-card">
                <div className="billing-stat-icon" style={{ background: `#6366f115`, color: '#6366f1' }}>
                  <MessageCircle size={20} />
                </div>
                <div>
                  <div className="billing-stat-label">Total Conversations</div>
                  <div className="billing-stat-value">{data.totals.conversations}</div>
                </div>
              </div>
              <div className="billing-stat-card">
                <div className="billing-stat-icon" style={{ background: `#22c55e15`, color: '#22c55e' }}>
                  <Receipt size={20} />
                </div>
                <div>
                  <div className="billing-stat-label">Billable Conversations</div>
                  <div className="billing-stat-value">{data.totals.billable_conversations}</div>
                </div>
              </div>
              <div className="billing-stat-card">
                <div className="billing-stat-icon" style={{ background: `#f59e0b15`, color: '#f59e0b' }}>
                  <DollarSign size={20} />
                </div>
                <div>
                  <div className="billing-stat-label">Estimated Cost</div>
                  <div className="billing-stat-value">₹{data.totals.estimated_cost_inr || 0}</div>
                </div>
              </div>
            </div>
          </>
        ) : null}

        <div style={{ marginTop: '24px', padding: '24px', background: 'white', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <h3 style={{ marginBottom: '12px' }}>Invoice & Payment History</h3>
          {invoices.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No invoices or paid entries yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Reference</th>
                    <th>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((item) => (
                    <tr key={item.id}>
                      <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{item.type || 'payment'}</td>
                      <td>{item.amount ? `₹${item.amount}` : '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{item.status || '—'}</td>
                      <td>{item.number || item.invoice_id || '—'}</td>
                      <td>
                        {item.download_url ? (
                          <a href={item.download_url} target="_blank" rel="noreferrer">Download</a>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>Not available</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </DashboardContainer>
  );
};

export default BillingPage;
